import { useState } from "react";
import {
  FaEdit, 
  FaCheck, 
  FaExternalLinkAlt, 
  FaCreditCard, 
  FaPlus, 
  FaTrashAlt,
  FaGift, 
  FaBolt,
} from "react-icons/fa";
import { useApi } from "../context/useApi";



const CreditCardDetails = ({ card, goBack }) => {
  const [editing, setEditing] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const { apiFetch } = useApi();


  const [form, setForm] = useState({
    card_name: card.name || "",
    card_type: card.card_type || "",
    card_network: card.card_network || "",
    website_link: card.website_link || "",
    lifetime_free: card.lifetime_free || "No",
    tags: card.tags || [],
  });

  const CARD_TYPES = [
    "Travel",
    "Rewards",
    "Fuel",
    "Cashback",
    "Lifestyle",
    "Business",
    "Student",
    "Premium",
    "Others",
  ];

  const CARD_NETWORKS = [
    "VISA",
    "Mastercard",
    "Amex",
    "RuPay",
    "Diners Club",
    "Discover",
    "UnionPay",
  ];

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const handleSave = async () => {
    setLoadingSave(true);
    try {
      const res = await apiFetch(
        `https://admin-backend-fbuuh6fo3q-el.a.run.app/banks/cards/${card.card_id}`,
        {
          method: "PUT",
          body: JSON.stringify(form),
        }
      );

      if (!res) return;
      const data = await res.json();

      if (res.ok) {
        setEditing(false);
        showToast("Card details saved successfully!", "success");
        // Update the card object if needed
        Object.assign(card, form);
      } else {
        showToast(data.error || "Failed to save card details", "error");
      }
    } catch (err) {
      console.error("Card update error:", err);
      showToast("Failed to save card details!", "error");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleCancel = () => {
    setForm({
      card_name: card.name || "",
      card_type: card.card_type || "",
      card_network: card.card_network || "",
      website_link: card.website_link || "",
      lifetime_free: card.lifetime_free || "No",
      tags: card.tags || [],
    });
    setEditing(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="text-gray-600 hover:text-gray-900 font-medium text-sm"
              >
                ← Cards
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {card.issuing_bank} - {card.name}
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {form.card_network} • {form.card_type}
                  </p>
                </div>
                {form.lifetime_free === "Yes" && (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                    Lifetime Free
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {form.website_link && (
                <a
                  href={form.website_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-sm text-gray-700 hover:text-gray-900 flex items-center gap-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <FaExternalLinkAlt size={14} />
                  View Website
                </a>
              )}
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
                >
                  <FaEdit size={14} />
                  Edit
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loadingSave}
                    className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
                  >
                    <FaCheck size={14} />
                    {loadingSave ? "Saving..." : "Save Changes"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Card Image & Meta */}
          <div className="col-span-3 space-y-4">
            {/* Card Image */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Card Image
              </h3>
              {card.image ? (
                <img
                  src={card.image}
                  alt={card.name}
                  className="w-full rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-full aspect-[1.6/1] bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <div className="text-center">
                    <FaCreditCard
                      className="mx-auto text-gray-400 mb-2"
                      size={24}
                    />
                    <p className="text-xs text-gray-500">No image</p>
                  </div>
                </div>
              )}
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Metadata
              </h3>
              <div className="space-y-3 text-xs">
                <div>
                  <p className="text-gray-500">Card ID</p>
                  <p className="text-gray-900 mt-1 font-mono text-[10px] break-all">
                    {card.card_id}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Last Updated</p>
                  <p className="text-gray-900 mt-1">
                    {card.last_updated
                      ? new Date(card.last_updated).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Rewards</p>
                  <p className="text-gray-900 mt-1">
                    {card.rewards?.length || 0}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Accelerated</p>
                  <p className="text-gray-900 mt-1">
                    {card.accelerated_rewards?.length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Content */}
          <div className="col-span-9 space-y-6">
            {/* Card Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Card Information
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Card Name"
                  value={form.card_name}
                  editing={editing}
                  onChange={(v) => setForm({ ...form, card_name: v })}
                  required
                />
                <Field
                  label="Card Type"
                  value={form.card_type}
                  editing={editing}
                  type="select"
                  options={CARD_TYPES}
                  onChange={(v) => setForm({ ...form, card_type: v })}
                />
                <Field
                  label="Network"
                  value={form.card_network}
                  editing={editing}
                  type="select"
                  options={CARD_NETWORKS}
                  onChange={(v) => setForm({ ...form, card_network: v })}
                />
                <Field
                  label="Lifetime Free"
                  value={form.lifetime_free}
                  editing={editing}
                  type="select"
                  options={["Yes", "No"]}
                  onChange={(v) => setForm({ ...form, lifetime_free: v })}
                />
                <div className="col-span-2">
                  <Field
                    label="Website URL"
                    value={form.website_link}
                    editing={editing}
                    onChange={(v) => setForm({ ...form, website_link: v })}
                    type="url"
                  />
                </div>
              </div>
            </div>

            {/* Tags Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                Tags
              </h2>
              {editing ? (
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={form.tags.join(", ")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tags: e.target.value
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="Enter tags separated by commas"
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  {form.tags.length > 0 ? (
                    form.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">
                      No tags assigned
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Rewards Configuration */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaGift size={16} className="text-gray-600" />
                  <h2 className="text-base font-semibold text-gray-900">
                    Rewards Configuration ({card.rewards?.length || 0})
                  </h2>
                </div>
                <button
                  onClick={() => showToast("Add reward feature coming soon")}
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <FaPlus size={12} />
                  Add Reward
                </button>
              </div>

              {card.rewards && card.rewards.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Reward Type
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Rate
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Spending Amount
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.rewards.map((reward, i) => (
                        <tr
                          key={reward.reward_id || i}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-3 px-4 text-gray-900">
                            {reward.reward_type}
                          </td>
                          <td className="py-3 px-4 text-gray-900 font-medium">
                            {reward.reward_rate}
                          </td>
                          <td className="py-3 px-4 text-gray-900">
                            ₹{reward.spending_amount.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button className="text-gray-400 hover:text-gray-700 p-2">
                              <FaEdit size={14} />
                            </button>
                            <button className="text-gray-400 hover:text-red-600 p-2">
                              <FaTrashAlt size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <FaGift className="mx-auto text-gray-400 mb-2" size={32} />
                  <p className="text-gray-500 text-sm">No rewards configured</p>
                  <button
                    onClick={() => showToast("Add reward feature coming soon")}
                    className="mt-3 text-sm text-gray-700 hover:text-gray-900 font-medium"
                  >
                    Add your first reward
                  </button>
                </div>
              )}
            </div>

            {/* Accelerated Rewards */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaBolt size={16} className="text-gray-600" />
                  <h2 className="text-base font-semibold text-gray-900">
                    Accelerated Rewards ({card.accelerated_rewards?.length || 0}
                    )
                  </h2>
                </div>
                <button
                  onClick={() =>
                    showToast("Add accelerated reward feature coming soon")
                  }
                  className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <FaPlus size={12} />
                  Add Accelerated
                </button>
              </div>

              {card.accelerated_rewards &&
              card.accelerated_rewards.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Vendor
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Multiplier
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                          Status
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {card.accelerated_rewards.map((ar, i) => (
                        <tr
                          key={ar.acc_reward_id || i}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="py-3 px-4 text-gray-900">
                            {ar.vendor}
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-semibold text-gray-900">
                              {ar.multiplier}x
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              Active
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button className="text-gray-400 hover:text-gray-700 p-2">
                              <FaEdit size={14} />
                            </button>
                            <button className="text-gray-400 hover:text-red-600 p-2">
                              <FaTrashAlt size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <FaBolt className="mx-auto text-gray-400 mb-2" size={32} />
                  <p className="text-gray-500 text-sm">
                    No accelerated rewards configured
                  </p>
                  <button
                    onClick={() =>
                      showToast("Add accelerated reward feature coming soon")
                    }
                    className="mt-3 text-sm text-gray-700 hover:text-gray-900 font-medium"
                  >
                    Add your first accelerated reward
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.message && (
        <div
          className={`fixed bottom-6 right-6 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${
            toast.type === "success" ? "bg-gray-900" : "bg-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

// Reusable Field Component
const Field = ({
  label,
  value,
  editing,
  onChange,
  type = "text",
  options = [],
  required = false,
}) => {
  if (!label && !editing) {
    return (
      <p className="text-sm text-gray-900 whitespace-pre-wrap">
        {value || "-"}
      </p>
    );
  }

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {editing ? (
        type === "select" ? (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          >
            <option value="">Select {label}</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        )
      ) : (
        <p className="text-sm text-gray-900 whitespace-pre-wrap">
          {String(value || "-")}
        </p>
      )}
    </div>
  );
};

export default CreditCardDetails;
